import { COLORS, FONT_FAMILIES } from '../../../config/constants';
import type { Point } from '../../../types';

export type { Point } from '../../../types';

export interface Size {
  width: number;
  height: number;
}

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type ShapeType =
  | 'rectangle'
  | 'ellipse'
  | 'diamond'
  | 'cylinder'
  | 'triangle'
  | 'connector';

export type DiagramTool =
  | 'select'
  | 'rectangle'
  | 'ellipse'
  | 'diamond'
  | 'cylinder'
  | 'triangle'
  | 'connector'
  | 'text'
  | 'draw';

export interface ShapeStyle {
  fill: string;
  stroke: string;
  strokeWidth: number;
  cornerRadius: number;
  opacity: number;
}

export interface TextStyle {
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  fontStyle: 'normal' | 'italic';
  textAlign: 'left' | 'center' | 'right';
  fontFamily: string;
  color: string;
}

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontSize: 24,
  fontWeight: 'normal',
  fontStyle: 'normal',
  textAlign: 'left',
  fontFamily: FONT_FAMILIES.ui,
  color: COLORS.black,
};

export interface ConnectorStyle {
  stroke: string;
  strokeWidth: number;
  lineStyle: 'solid' | 'dashed' | 'dotted';
  startArrow: ArrowHead;
  endArrow: ArrowHead;
}

export type ArrowHead = 'none' | 'arrow' | 'diamond' | 'circle';

export interface ConnectionPoint {
  id: string;
  position: Point;
  direction: 'top' | 'right' | 'bottom' | 'left';
}

export type HandlePosition =
  | 'top-left'
  | 'top'
  | 'top-right'
  | 'right'
  | 'bottom-right'
  | 'bottom'
  | 'bottom-left'
  | 'left';

export interface Handle {
  position: HandlePosition;
  point: Point;
}

export const DEFAULT_SHAPE_STYLE: ShapeStyle = {
  fill: COLORS.white,
  stroke: COLORS.ink,
  strokeWidth: 2,
  cornerRadius: 8,
  opacity: 1,
};

export const DEFAULT_CONNECTOR_STYLE: ConnectorStyle = {
  stroke: COLORS.ink,
  strokeWidth: 2,
  lineStyle: 'solid',
  startArrow: 'none',
  endArrow: 'arrow',
};

export const SHAPE_PRESETS: Record<string, Partial<ShapeStyle>> = {
  default: { fill: '#ffffff', stroke: '#374151' },
  primary: { fill: '#dbeafe', stroke: '#2563eb' },
  success: { fill: '#dcfce7', stroke: '#16a34a' },
  warning: { fill: '#fef3c7', stroke: '#d97706' },
  danger: { fill: '#fee2e2', stroke: '#dc2626' },
  purple: { fill: '#f3e8ff', stroke: '#9333ea' },
  dark: { fill: '#374151', stroke: '#1f2937' },
};

export const SHAPE_PRESETS_DARK: Record<string, Partial<ShapeStyle>> = {
  default: { fill: '#374151', stroke: '#9ca3af' },
  primary: { fill: '#1e3a5f', stroke: '#60a5fa' },
  success: { fill: '#14532d', stroke: '#4ade80' },
  warning: { fill: '#451a03', stroke: '#fbbf24' },
  danger: { fill: '#450a0a', stroke: '#f87171' },
  purple: { fill: '#3b0764', stroke: '#c084fc' },
  light: { fill: '#e5e7eb', stroke: '#f3f4f6' },
};

// Mapping between light and dark theme colors (bidirectional)
export const THEME_COLOR_MAP: Record<string, string> = {
  // Black/White for text elements
  '#000000': '#ffffff', '#000': '#ffffff',
  // Default
  '#ffffff': '#374151', '#374151': '#ffffff',
  '#1a1a1a': '#9ca3af', '#9ca3af': '#1a1a1a',
  // Primary
  '#dbeafe': '#1e3a5f', '#1e3a5f': '#dbeafe',
  '#2563eb': '#60a5fa', '#60a5fa': '#2563eb',
  // Success
  '#dcfce7': '#14532d', '#14532d': '#dcfce7',
  '#16a34a': '#4ade80', '#4ade80': '#16a34a',
  // Warning
  '#fef3c7': '#451a03', '#451a03': '#fef3c7',
  '#d97706': '#fbbf24', '#fbbf24': '#d97706',
  // Danger
  '#fee2e2': '#450a0a', '#450a0a': '#fee2e2',
  '#dc2626': '#f87171', '#f87171': '#dc2626',
  // Purple
  '#f3e8ff': '#3b0764', '#3b0764': '#f3e8ff',
  '#9333ea': '#c084fc', '#c084fc': '#9333ea',
  // Dark/Light preset
  '#1f2937': '#e5e7eb', '#e5e7eb': '#1f2937',
  '#f3f4f6': '#374151',
  // Additional common colors
  '#e5e5e5': '#2a2a2f', '#2a2a2f': '#e5e5e5',
};
